// /notifications/broadcasts — Admin Broadcast Center (G3.6)
//
// Compose + history for SYSTEM_ANNOUNCEMENT broadcasts. Three role modes:
//   - SUPER_ADMIN / ADMIN → platform broadcasts (all tenants or targeted)
//   - OWNER               → tenant-wide staff memos (own tenant only)
//   - MODERATOR           → read-only history
//
// Vertical: SHARED. No feature flag. Min role level: 50 (OWNER).
// Traceability: FR-NOTIF-010, PAGES.md §3.72 (extension)

import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, PageContainer } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, cn } from '@/constants/design-tokens';
import { BroadcastComposeCard } from './BroadcastComposeCard';
import { BroadcastHistoryTable } from './BroadcastHistoryTable';
import {
  notificationService,
  type BroadcastStats,
} from '@/services/notification.service';

type Mode = 'platform' | 'owner' | 'viewer';

function resolveMode(role: string | undefined): Mode {
  if (role === 'SUPER_ADMIN' || role === 'ADMIN') return 'platform';
  if (role === 'OWNER') return 'owner';
  return 'viewer'; // MODERATOR fallback
}

interface KpiTone {
  wrapper: string;
  label: string;
  value: string;
  accent: string;
}

const KPI_TONES: Record<'violet' | 'indigo' | 'emerald', KpiTone> = {
  violet: {
    wrapper:
      "before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-violet-500",
    label: 'text-violet-700 dark:text-violet-300',
    value: 'text-neutral-900 dark:text-white',
    accent: 'bg-violet-50/70 dark:bg-violet-900/20 text-violet-600 dark:text-violet-300',
  },
  indigo: {
    wrapper:
      "before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-indigo-500",
    label: 'text-indigo-700 dark:text-indigo-300',
    value: 'text-neutral-900 dark:text-white',
    accent: 'bg-indigo-50/70 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300',
  },
  emerald: {
    wrapper:
      "before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-emerald-500",
    label: 'text-emerald-700 dark:text-emerald-300',
    value: 'text-neutral-900 dark:text-white',
    accent: 'bg-emerald-50/70 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-300',
  },
};

function Kpi({
  tone,
  icon,
  label,
  value,
  suffix,
}: {
  tone: keyof typeof KPI_TONES;
  icon: string;
  label: string;
  value: string;
  suffix?: string;
}) {
  const t = KPI_TONES[tone];
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl bg-white/80 dark:bg-neutral-900/60 backdrop-blur-sm p-4 shadow-[0_8px_24px_-14px_rgba(15,23,42,0.18)]',
        t.wrapper,
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
            t.accent,
          )}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={icon} />
          </svg>
        </div>
        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', t.label)}>
          {label}
        </p>
      </div>
      <p className={cn('mt-3 text-2xl font-bold tabular-nums', t.value)}>
        {value}
        {suffix && (
          <span className="ml-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {suffix}
          </span>
        )}
      </p>
    </div>
  );
}

function BroadcastCenterContent() {
  const profile = useAuthStore((s) => s.profile);
  const mode = useMemo(() => resolveMode(profile?.role), [profile?.role]);

  const [tab, setTab] = useState<'compose' | 'history'>(
    mode === 'viewer' ? 'history' : 'compose',
  );
  const [refreshToken, setRefreshToken] = useState(0);

  // KPIs — aggregated server-side over the FULL broadcast history (not just
  // the first page), so they stay correct once history exceeds one page.
  // Fetched independently so KPI values stay stable while the history table
  // paginates.
  const [stats, setStats] = useState<BroadcastStats>({
    sent_today: 0,
    sent_last_7_days: 0,
    total_reach: 0,
  });

  useEffect(() => {
    document.title = 'Broadcast Center — Notifications — Orio360 Admin';
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await notificationService.getBroadcastStats();
        if (cancelled) return;
        setStats(res);
      } catch {
        // Silent — KPIs degrade gracefully to zeros.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const sentToday = stats.sent_today;
  const sentWeek = stats.sent_last_7_days;
  const totalReach = stats.total_reach;

  const handleSent = () => {
    setRefreshToken((n) => n + 1);
    setTab('history');
  };

  const composeDisabled = mode === 'viewer';

  return (
    <PageContainer
      title="Broadcast Center"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Notifications', href: '/notifications' },
        { label: 'Broadcasts' },
      ]}
    >
      <div className="animate-fade-in">
        {/* Fintech-weight gradient header */}
        <div className="bg-gradient-to-r from-violet-700 via-indigo-700 to-sky-700 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(79,70,229,0.55)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-60 w-60 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white">Broadcast Center</h1>
              <p className="text-white/80 mt-1 text-sm max-w-2xl">
                {mode === 'platform'
                  ? 'Compose platform-wide system announcements or target a single tenant. Every send is audited.'
                  : mode === 'owner'
                    ? 'Memo every active user in your tenant — staff meetings, policy changes, schedule updates.'
                    : 'Audit view of broadcasts sent across Orio360. Compose access is restricted to SUPER_ADMIN / ADMIN and tenant OWNER roles.'}
              </p>
            </div>
            <div className="hidden sm:flex shrink-0 items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm px-3 py-1.5 text-[11px] font-semibold tabular-nums">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              Role · {profile?.role ?? '—'}
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid gap-4 sm:grid-cols-3 mb-6">
          <Kpi
            tone="violet"
            icon="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            label="Sent today"
            value={sentToday.toLocaleString('en-IN')}
          />
          <Kpi
            tone="indigo"
            icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            label="Last 7 days"
            value={sentWeek.toLocaleString('en-IN')}
            suffix="broadcasts"
          />
          <Kpi
            tone="emerald"
            icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            label="Estimated reach"
            value={totalReach.toLocaleString('en-IN')}
          />
        </div>

        {/* Tabs — Compose is hidden entirely for read-only viewers (MODERATOR);
            the role badge in the header already explains the audit-only context. */}
        <div className={cn(GLASS, 'p-1 inline-flex gap-1 mb-4')}>
          {!composeDisabled && (
            <button
              type="button"
              onClick={() => setTab('compose')}
              className={cn(
                'px-4 py-1.5 rounded-xl text-xs font-semibold transition-all',
                tab === 'compose'
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-[0_6px_18px_-8px_rgba(99,102,241,0.6)]'
                  : 'text-neutral-600 dark:text-neutral-300 hover:bg-white/40 dark:hover:bg-neutral-800/40',
              )}
            >
              Compose
            </button>
          )}
          <button
            type="button"
            onClick={() => setTab('history')}
            className={cn(
              'px-4 py-1.5 rounded-xl text-xs font-semibold transition-all',
              tab === 'history'
                ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-[0_6px_18px_-8px_rgba(79,70,229,0.6)]'
                : 'text-neutral-600 dark:text-neutral-300 hover:bg-white/40 dark:hover:bg-neutral-800/40',
            )}
          >
            History
          </button>
        </div>

        {tab === 'compose' ? (
          <BroadcastComposeCard mode={mode} onSent={handleSent} />
        ) : (
          <BroadcastHistoryTable refreshToken={refreshToken} />
        )}
      </div>
    </PageContainer>
  );
}

export function BroadcastCenterPage() {
  return (
    <ErrorBoundary>
      <BroadcastCenterContent />
    </ErrorBoundary>
  );
}
